import Color from 'color';
import inViewport from 'in-viewport';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import classNames from 'clsx';
import { Box } from './Box';
/*
 * Clamp a value between two other values
 * @param {number} number
 * @param {number} min
 * @param {number} max
 * @return {number}
 */
export function clamp(number, min = 0, max = 1) {
  return Math.min(Math.max(number, min), max);
}

/*
 * Get the percentage scrolled over an element
 * @param {HTMLElement} element
 * @return {number} value between 0 and 1
 */
export function scrolledOverPercent(element) {
  const scrolled = window.pageYOffset;
  const height = element.offsetHeight;
  const top = element.offsetTop;
  const percent = scrolled / (top + height);

  return clamp(percent);
}

/*
 * Resize to original aspect ratio
 * @param {Object} dimensions The original dimensions
 * @param {number} dimensions.height
 * @param {number} dimensions.width
 * @param {string} prop Property name
 * @param {number} value Current property value
 * @param {Object} new height and width
 */
export function resizeToRatio(dimensions, prop, value) {
  const otherProp = prop === 'width' ? 'height' : 'width';
  const otherPropValue = Math.round((value * dimensions[otherProp]) / dimensions[prop]);

  return {
    [otherProp]: otherPropValue,
    [prop]: value,
  };
}

/*
 * Simulate "background-position: contain"
 * @param {Object} dimensions The original dimensions
 * @param {number} dimensions.height
 * @param {number} dimensions.width
 * @param {Object} maxDimensions The available space
 * @param {number} maxDimensions.height
 * @param {number} maxDimensions.width
 * @return {Object} new height and width
 */
export function resizeToContain(dimensions, maxDimensions) {
  return Object.keys(dimensions).reduce((prevDimensions, prop) => (
    prevDimensions[prop] > maxDimensions[prop]
      ? resizeToRatio(prevDimensions, prop, maxDimensions[prop])
      : prevDimensions
  ), dimensions);
}

/*
 * Simulate "background-position: cover"
 * @param {Object} dimensions The original dimensions
 * @param {number} dimensions.height
 * @param {number} dimensions.width
 * @param {Object} maxDimensions The available space
 * @param {number} maxDimensions.height
 * @param {number} maxDimensions.width
 * @return {Object} new height and width
 */
export function resizeToCover(dimensions, maxDimensions) {
  const dimensionsAfterContain = resizeToContain(dimensions, maxDimensions);

  return Object.keys(dimensions).reduce((prevDimensions, prop) => (
    prevDimensions[prop] < maxDimensions[prop]
      ? resizeToRatio(prevDimensions, prop, maxDimensions[prop])
      : prevDimensions
  ), dimensionsAfterContain);
}

const cover = {
  position: 'absolute',
  top: '0',
  right: '0',
  bottom: '0',
  left: '0',
};

const useStyles = () => ({
  root: {
    position: 'relative',
    minHeight: ({ minHeight = '50vh' }) => minHeight,
  },
  cover,

  overlay: {
    display: 'flex',
    justifyContent: ({ isCentered = true }) => (isCentered ? 'center' : 'flex-start'),
    alignItems: ({ isCentered = true }) => (isCentered ? 'center' : 'stretch'),
    textAlign: ({ isCentered = true }) => (isCentered ? 'center' : 'left'),
    backgroundColor: ({ color = '#fff', opacity = 0.8 }) => Color(color).alpha(opacity).rgb().string(),
  },
});

const imgStyles = makeStyles(() => (
  ({
    cover,
    img: {
      backgroundAttachment: ({ isFixed = false }) => (isFixed ? 'fixed' : 'scroll'),
      backgroundImage: ({ imageSrc }) => `url(${imageSrc})`,
      backgroundPosition: 'center',
      backgroundEepeat: 'no-repeat',
      backgroundSize: 'cover',
      opacity: ({ isVisible }) => (isVisible ? 1 : 0),
      transitionDuration: ({ transitionDuration = 600 }) => `${transitionDuration}ms`,
      transitionProperty: 'opacity',
      transitionTimingFunction: ({ transitionTimingFunction = 'transitionTimingFunction' }) => transitionTimingFunction,
    },
  })
));

function Img(props) {
  const { style } = props;
  const classes = imgStyles(props);
  return <Box className={classNames(classes.img, classes.cover)} sx={style} />;
}

class LazyHero extends Component {
  constructor() {
    super();
    this.state = {
      backgroundPositionY: 'center',
      backgroundDimensions: null,
      heroDimensions: null,
      image: null,
      isInViewport: false,
    };
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
    this.updateSize = this.updateSize.bind(this);
  }

  componentDidMount() {
    inViewport(this.ref, () => this.setState({ isInViewport: true }));
    this.loadImage();
    const { parallaxOffset = 0 } = this.props;

    if (parallaxOffset > 0) {
      window.addEventListener('scroll', this.handleScroll);
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentDidUpdate(prevProps) {
    const { imageSrc } = this.props;
    if (prevProps.imageSrc !== imageSrc) {
      this.loadImage();
    }
  }

  componentWillUnmount() {
    const { parallaxOffset = 0 } = this.props;
    if (parallaxOffset > 0) {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
    }
  }

  handleScroll() {
    this.updatePosition();
  }

  handleResize() {
    this.updateSize();
    this.updatePosition();
  }

  loadImage() {
    const { imageSrc, parallaxOffset = 0 } = this.props;
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      this.setState({ image });

      if (parallaxOffset > 0) {
        this.updateSize();
        this.updatePosition();
      }
    };
  }

  updateSize() {
    const {
      image,
    } = this.state;
    const {
      offsetHeight,
      offsetWidth,
    } = this.ref;
    const {
      parallaxOffset = 0,
    } = this.props;
    if (!image) return;

    const heroDimensions = {
      height: offsetHeight,
      width: offsetWidth,
    };

    const imageDimensions = {
      height: image.height,
      width: image.width,
    };

    const resizedImage = resizeToCover(imageDimensions, heroDimensions);
    const initialVisibleImageHeight = resizedImage.height - parallaxOffset;

    const minHeight = initialVisibleImageHeight < heroDimensions.height
      ? resizedImage.height + heroDimensions.height - initialVisibleImageHeight
      : resizedImage.height;

    const finalHeight = minHeight + (this.ref.offsetTop * 2);
    const backgroundDimensions = resizeToCover(imageDimensions, { height: finalHeight });
    this.setState({ backgroundDimensions, heroDimensions });
  }

  updatePosition() {
    const {
      backgroundDimensions,
      heroDimensions,
    } = this.state;
    const {
      parallaxOffset = 0,
    } = this.props;
    if (!backgroundDimensions) return;
    const position = 0
      + this.ref.offsetTop
      // Center image vertically
      - (backgroundDimensions.height / 2)
      + (heroDimensions.height / 2)
      - (parallaxOffset / 2)
      // Apply scroll position
      + (parallaxOffset * scrolledOverPercent(this.ref));

    this.setState({ backgroundPositionY: `${Math.round(position)}px` });
  }

  render() {
    // eslint-disable-next-line no-unused-vars
    const { backgroundDimensions, backgroundPositionY } = this.state;
    const {
      classes,
      style,
      transitionTimingFunction = 'ease-in-out',
      transitionDuration = 600,
      isFixed = false,
      imageSrc,
      parallaxOffset = 0,
      color = '#fff',
      children,
    } = this.props;
    const {
      image,
      isInViewport,
    } = this.state;
    return (
      <Box
        className={classes.root}
        ref={(r) => { this.ref = r; }}
        sx={style}
      >
        <Img
          isVisible={image && isInViewport}
          isFixed={isFixed || parallaxOffset > 0}
          imageHeight={backgroundDimensions && backgroundDimensions.height}
          imageSrc={imageSrc}
          imageWidth={backgroundDimensions && backgroundDimensions.width}
          transitionDuration={transitionDuration}
          transitionTimingFunction={transitionTimingFunction}
        />
        <div
          className={classNames(classes.cover, classes.overlay)}
          color={color}
        >
          {children}
        </div>
      </Box>
    );
  }
}

const LazyHeroWithStyles = withStyles(useStyles)(LazyHero);

LazyHeroWithStyles.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.string,
  imageSrc: PropTypes.string,
  isCentered: PropTypes.bool,
  isFixed: PropTypes.bool,
  minHeight: PropTypes.string,
  opacity: PropTypes.number,
  parallaxOffset: PropTypes.number,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  transitionDuration: PropTypes.number,
  transitionTimingFunction: PropTypes.string,
};

export default LazyHeroWithStyles;
